<template>
    <div class="past-event-item / flex" :data-testid="`project-past-event-${index}`" :data-event="event.eventType" :data-author="event.author.type">
        <div class="past-event-item_label / flex">
            <VJoyAvatar class="past-event-item_label_avatar" :full-name="event.author.fullName" :photo-url="event.author.photoUrl" size="small" />
            <LabelContent
                v-if="!isOverflowing"
                ref="pastEventLabelRef"
                data-testid="project-past-event-title"
                class="past-event-item_label_content"
                :event="event"
            />

            <VJoyTooltip v-else placement="bottom" variant="secondary">
                <LabelContent ref="pastEventLabelRef" class="past-event-item_label_content" :event="event" />
                <template #tooltip-content>
                    <LabelContent ref="pastEventLabelRef" wrap class="past-event-item_label_content" :event="event" />
                </template>
            </VJoyTooltip>
        </div>

        <span ref="pastEventItem" class="past-event-item_date" data-testid="project-past-event-date">
            {{ isHovering ? formatDateToLongWithTimeToShort(event.eventDate) : formatDate(event.eventDate) }}
        </span>
    </div>

    <hr />
</template>

<script setup lang="ts">
    import {watch, useTemplateRef, type Ref} from 'vue';
    import {useFormat} from '#imports';
    import {useContentOverflow, useMouseHover} from '@malt/utils';
    import {VJoyAvatar, VJoyTooltip} from '@maltjoy/core-vue';
    import type {ProjectActivityResourcePastEventsInner} from '@api';
    import LabelContent from './LabelContent.vue';

    defineProps<{
        event: ProjectActivityResourcePastEventsInner;
        index: number;
    }>();

    const {formatDate, formatDateToLongWithTimeToShort} = useFormat();

    // handle hover state to show date and time
    const pastEventItem = useTemplateRef('pastEventItem');
    const {isHovering} = useMouseHover({customRef: pastEventItem as Ref<HTMLElement | null>});

    // handle long strings
    const pastEventLabelRef = useTemplateRef('pastEventLabelRef');
    const {isOverflowing, startObserving, stopObserving} = useContentOverflow({
        root: pastEventLabelRef as unknown as Ref<HTMLElement | undefined>,
        immediate: false,
    });

    watch(
        isOverflowing,
        () => {
            stopObserving();
            startObserving();
        },
        {immediate: true},
    );
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .past-event-item {
        flex-flow: row nowrap;
        align-items: center;
        justify-content: space-between;
        margin: var(--joy-core-spacing-4) 0;
        column-gap: var(--joy-core-spacing-2);

        &_label {
            flex: 1 1 auto;
            flex-flow: row nowrap;
            align-items: center;
            column-gap: var(--joy-core-spacing-2);

            &_avatar {
                flex: 0 0 auto;
            }

            :deep(.joy-tooltip__wrapper) {
                max-width: 100%;
            }

            &_content {
                text-wrap: nowrap;
                flex: 1 1 auto;
                max-width: 100%;

                @include text.getFontBySize(primary-300);
            }
        }

        &_date {
            color: var(--joy-color-neutral-40);
            flex: 0 0 auto;

            @include text.getFontBySize(primary-200);
        }
    }

    hr {
        border: 0;
        border-top: 1px solid var(--joy-color-neutral-30);
    }
</style>
